import { launchScriptSections, narrationWordCount } from "@veyso/core";
import type { LaunchVersionView } from "../api.js";

/**
 * The studio's arithmetic, kept apart from its components so the tests at the
 * root can run it without React.
 */

/** Words, counted the way the script budget counts them. */
export function wordCount(text: string, pace?: number): number {
  return narrationWordCount(text, pace);
}

/** How many of the film's parts the script is in: its paragraphs. */
export function scriptParts(text: string): number {
  return launchScriptSections(text).length;
}

/** What the chat calls the versions nobody typed into it. */
const EVENTS: Record<Exclude<LaunchVersionView["source"], "chat">, string> = {
  first: "First version",
  script: "Made from your script edits",
  retry: "Tried again",
};

export interface ChatTurn {
  role: "customer" | "veyso" | "event";
  text: string;
  version: number;
  priceUsd: number | null;
  removed: boolean;
  /** Set on the request (or event) of a version that could not be made. */
  failed?: boolean;
  /** Why it could not be made, when the job said. */
  error?: string | null;
}

/**
 * The chat, oldest first, rebuilt from the versions.
 *
 * Each change asked for in words is the customer's turn, answered by what
 * changed once the version exists. The first version and a script edit were
 * not typed into the chat, so they read as events, as does a failed first
 * film tried again. A failed version keeps its request or event — there is
 * nothing to answer it with — marked so the page can say nothing was charged.
 */
export function chatTurns(versions: LaunchVersionView[]): ChatTurn[] {
  const turns: ChatTurn[] = [];
  for (const version of [...versions].sort((a, b) => a.number - b.number)) {
    const removed = version.status === "removed";
    const priceUsd = version.status === "ready" || removed ? version.priceUsd : null;
    const failed = version.status === "failed" ? { failed: true, error: version.error } : {};

    // Told apart by how the version came about, never by its words: a customer
    // who types "Tried again" into the chat is still a customer.
    // A later version from a job queued before versions existed is a "first"
    // by source; it is named by what the worker recorded rather than as v1.
    const event =
      version.source === "chat"
        ? null
        : version.source === "first" && version.number > 1
          ? version.request
          : EVENTS[version.source];
    if (event) {
      turns.push({ role: "event", text: event, version: version.number, priceUsd, removed, ...failed });
      continue;
    }

    turns.push({
      role: "customer",
      text: version.request,
      version: version.number,
      priceUsd: null,
      removed,
      ...failed,
    });
    if ((version.status === "ready" || removed) && version.summary) {
      turns.push({ role: "veyso", text: version.summary, version: version.number, priceUsd, removed });
    }
  }
  return turns;
}

/** The version every new one is made from: the newest that finished. */
export function newestReady(versions: LaunchVersionView[]): LaunchVersionView | null {
  return versions.filter((version) => version.status === "ready").sort((a, b) => b.number - a.number)[0] ?? null;
}

/** How long ago, short enough for a 168px card. */
export function ago(iso: string, now = Date.now()): string {
  const minutes = Math.round((now - new Date(iso).getTime()) / 60_000);
  if (minutes < 1) return "Just now";
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} h ago`;
  const days = Math.round(hours / 24);
  return days === 1 ? "Yesterday" : `${days} days ago`;
}
